import { useEffect, useRef, useState } from "react";

import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";

import { isRequestAbortError } from "../api/deals";
import {
  createDealMyFinancePayment,
  deleteDealMyFinancePayment,
  fetchDealMyFinance,
  patchDealMyFinance,
  patchDealMyFinancePayment,
} from "../api/dealPrivateFinance";
import { formatMoney } from "../lib/dealFormatting";
import {
  getPrivateFinanceIncomeTypeLabel,
  getPrivateFinancePaymentStatusLabel,
} from "../lib/privateFinanceLabels";
import DealWorkspaceSectionHeader from "./DealWorkspaceSectionHeader";

const CURRENCY_OPTIONS = ["USD", "EUR", "UAH"];

const emptyPaymentDraft = {
  amount: "",
  received_at: "",
  note: "",
};

const defaultFixedCurrency = (dealCurrency) =>
  (CURRENCY_OPTIONS.includes(dealCurrency) ? dealCurrency : "UAH");

const financeToForm = (finance, dealCurrency) => {
  const incomeType = finance?.income_type || "";
  return {
    income_type: incomeType,
    income_value: finance?.income_value || "",
    income_currency:
      incomeType === "percent"
        ? (dealCurrency || "")
        : (finance?.income_currency || defaultFixedCurrency(dealCurrency)),
    expected_payment_date: finance?.expected_payment_date || "",
    note: finance?.note || "",
  };
};

const normalizeFinance = (finance) => ({
  ...(finance || {}),
  configured: Boolean(finance && finance.configured),
  payments: Array.isArray(finance?.payments) ? finance.payments : [],
});

const formatDateUk = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("uk-UA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const effectiveFinanceCurrency = (finance, dealCurrency) => {
  if (finance?.income_type === "percent") return dealCurrency || finance.income_currency || "";
  return finance?.income_currency || dealCurrency || "";
};

/**
 * Non-authoritative percent preview for unconfigured onboarding only.
 * Blank / zero / invalid inputs return null. Never persisted.
 */
export function previewPercentExpectedAmount(dealPrice, percentValue) {
  if (dealPrice == null || dealPrice === "" || percentValue == null || percentValue === "") {
    return null;
  }
  const price = Number(dealPrice);
  const percent = Number(percentValue);
  if (!Number.isFinite(price) || !Number.isFinite(percent)) return null;
  if (price <= 0 || percent < 0) return null;
  const raw = (price * percent) / 100;
  if (!Number.isFinite(raw)) return null;
  return (Math.round(raw * 100) / 100).toFixed(2);
};

/**
 * Progress ratio from authoritative expected/received only.
 * Never returns NaN/Infinity; fill never exceeds the track.
 */
export function computeFinanceProgressRatio(expectedAmount, receivedTotal) {
  const expected = Number(expectedAmount);
  const received = Number(receivedTotal);
  if (!Number.isFinite(expected) || expected <= 0) {
    return 0;
  }
  if (!Number.isFinite(received) || received <= 0) {
    return 0;
  }
  const ratio = received / expected;
  if (!Number.isFinite(ratio) || ratio < 0) return 0;
  return Math.min(ratio, 1);
}

/**
 * Meaningful optional personal details: non-empty date OR trimmed note.
 * Whitespace-only note is not meaningful.
 */
export function hasMeaningfulOptionalDetails(form) {
  const hasDate = Boolean(
    form?.expected_payment_date != null
    && String(form.expected_payment_date).trim() !== "",
  );
  const hasNote = Boolean(
    form?.note != null && String(form.note).trim() !== "",
  );
  return hasDate || hasNote;
}

/**
 * Empty unconfigured finance form must not PATCH (B1 no-op contract).
 * income_type blank AND no meaningful optional date/note.
 */
export function isEmptyUnconfiguredFinanceNoOp(form, configured) {
  if (configured) return false;
  if (form?.income_type) return false;
  return !hasMeaningfulOptionalDetails(form);
}

function DealPrivateFinanceSection({ dealId, dealCurrency, dealPrice = null }) {
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [finance, setFinance] = useState(null);
  const [retryToken, setRetryToken] = useState(0);

  const [financeForm, setFinanceForm] = useState(() =>
    financeToForm(null, dealCurrency),
  );
  const [financeSubmitState, setFinanceSubmitState] = useState("idle");
  const [financeFieldErrors, setFinanceFieldErrors] = useState({});
  const [financeFormError, setFinanceFormError] = useState(null);
  const [financeSuccess, setFinanceSuccess] = useState(null);
  const [planEditorOpen, setPlanEditorOpen] = useState(false);
  const [paymentFormOpen, setPaymentFormOpen] = useState(false);

  const [paymentDraft, setPaymentDraft] = useState(emptyPaymentDraft);
  const [paymentSubmitState, setPaymentSubmitState] = useState("idle");
  const [paymentFieldErrors, setPaymentFieldErrors] = useState({});
  const [paymentFormError, setPaymentFormError] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyPaymentDraft);
  const [deleteError, setDeleteError] = useState(null);
  const [paymentDeleteConfirmId, setPaymentDeleteConfirmId] = useState(null);

  const mountedRef = useRef(true);
  const activeDealIdRef = useRef(dealId);
  const mutationGenerationRef = useRef(0);
  const financeSubmittingRef = useRef(false);
  const paymentSubmittingRef = useRef(false);
  const mutationControllerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      mutationGenerationRef.current += 1;
      if (mutationControllerRef.current) {
        mutationControllerRef.current.abort();
        mutationControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    activeDealIdRef.current = dealId;
    mutationGenerationRef.current += 1;
    if (mutationControllerRef.current) {
      mutationControllerRef.current.abort();
      mutationControllerRef.current = null;
    }
    setPlanEditorOpen(false);
    setPaymentFormOpen(false);
    setEditingPaymentId(null);
    setEditDraft(emptyPaymentDraft);
    setPaymentDraft(emptyPaymentDraft);
    setPaymentDeleteConfirmId(null);
  }, [dealId]);

  const isStale = (generation) => (
    !mountedRef.current
    || generation !== mutationGenerationRef.current
    || activeDealIdRef.current !== dealId
  );

  const applyFinance = (payload) => {
    const normalized = normalizeFinance(payload);
    setFinance(normalized);
    setFinanceForm(financeToForm(normalized, dealCurrency));
  };

  useEffect(() => {
    if (dealId == null || dealId === "") {
      setLoadState("idle");
      setFinance(null);
      return undefined;
    }

    const controller = new AbortController();
    const generation = mutationGenerationRef.current;
    setLoadState("loading");
    setLoadError(null);

    fetchDealMyFinance(dealId, { signal: controller.signal })
      .then((payload) => {
        if (controller.signal.aborted || isStale(generation)) return;
        applyFinance(payload);
        setLoadState("loaded");
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (isStale(generation)) return;
        setFinance(null);
        setLoadError(messageFromError(error));
        setLoadState("error");
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, dealCurrency, retryToken]);

  const refetchFinanceAuthoritative = async (generation) => {
    const payload = await fetchDealMyFinance(dealId);
    if (isStale(generation)) return false;
    applyFinance(payload);
    setLoadState("loaded");
    setLoadError(null);
    return true;
  };

  const financeCurrency = effectiveFinanceCurrency(finance, dealCurrency);
  const payments = finance?.payments || [];
  const configured = Boolean(finance?.configured);
  const hasPayments = payments.length > 0;
  const financeBusy = financeSubmitState === "submitting";
  const paymentBusy = paymentSubmitState === "submitting";
  const showPlanEditor = !configured || planEditorOpen;
  const percentPreview = (
    financeForm.income_type === "percent"
      ? previewPercentExpectedAmount(dealPrice, financeForm.income_value)
      : null
  );
  const progressRatio = computeFinanceProgressRatio(
    finance?.expected_amount,
    finance?.received_total,
  );
  const progressPercent = Math.round(progressRatio * 100);
  const financeOverpaid = (
    finance?.payment_status === "overpaid" || finance?.overpaid === true
  );
  const meaningfulOptionalDetails = hasMeaningfulOptionalDetails(financeForm);
  const emptyUnconfiguredNoOp = isEmptyUnconfiguredFinanceNoOp(
    financeForm,
    configured,
  );
  const canSubmitFinance = !emptyUnconfiguredNoOp;
  // Personal-details wording is only for unconfigured optional-only saves.
  // Configured reset (Не налаштовано) keeps «Зберегти план доходу».
  const financeSubmitLabel = (
    !configured
    && !financeForm.income_type
    && meaningfulOptionalDetails
      ? "Зберегти особисті деталі"
      : "Зберегти план доходу"
  );

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
  };

  const handleIncomeTypeChange = (incomeType) => {
    if (hasPayments && incomeType === "") {
      return;
    }
    setFinanceSuccess(null);
    setFinanceFormError(null);
    setFinanceFieldErrors({});
    setFinanceForm((current) => ({
      ...current,
      income_type: incomeType,
      income_value: incomeType ? current.income_value : "",
      income_currency:
        incomeType === "percent"
          ? (dealCurrency || "")
          : (incomeType === "fixed"
            ? (current.income_currency || defaultFixedCurrency(dealCurrency))
            : ""),
    }));
  };

  const buildFinancePayload = () => ({
    income_type: financeForm.income_type,
    income_value: financeForm.income_type ? financeForm.income_value : null,
    income_currency:
      financeForm.income_type === "percent"
        ? (dealCurrency || "")
        : (financeForm.income_type === "fixed" ? financeForm.income_currency : ""),
    expected_payment_date: financeForm.expected_payment_date || null,
    note: financeForm.note,
  });

  const handleFinanceSubmit = async (event) => {
    event.preventDefault();
    if (isEmptyUnconfiguredFinanceNoOp(financeForm, configured)) {
      return;
    }
    if (financeSubmittingRef.current) return;
    financeSubmittingRef.current = true;
    const generation = mutationGenerationRef.current;
    const controller = new AbortController();
    mutationControllerRef.current = controller;

    setFinanceSubmitState("submitting");
    setFinanceFormError(null);
    setFinanceFieldErrors({});
    setFinanceSuccess(null);

    try {
      await patchDealMyFinance(dealId, buildFinancePayload(), {
        signal: controller.signal,
      });
      if (controller.signal.aborted || isStale(generation)) return;
      const refreshed = await refetchFinanceAuthoritative(generation);
      if (!refreshed) return;
      setFinanceSubmitState("idle");
      setFinanceSuccess(
        !configured
        && !financeForm.income_type
        && hasMeaningfulOptionalDetails(financeForm)
          ? "Особисті деталі збережено."
          : "План доходу збережено.",
      );
      setPlanEditorOpen(false);
    } catch (error) {
      if (controller.signal.aborted || isRequestAbortError(error)) return;
      if (isStale(generation)) return;
      const parsed = parseDrfFieldErrors(error, [
        "income_type",
        "income_value",
        "income_currency",
        "expected_payment_date",
        "note",
        "finance",
      ]);
      setFinanceFieldErrors(parsed);
      setFinanceFormError(parsed._form || messageFromError(error));
      setFinanceSubmitState("error");
    } finally {
      if (mutationControllerRef.current === controller) {
        mutationControllerRef.current = null;
      }
      financeSubmittingRef.current = false;
    }
  };

  const handleCreatePayment = async (event) => {
    event.preventDefault();
    if (!configured || paymentSubmittingRef.current) return;
    paymentSubmittingRef.current = true;
    const generation = mutationGenerationRef.current;
    const controller = new AbortController();
    mutationControllerRef.current = controller;

    setPaymentSubmitState("submitting");
    setPaymentFormError(null);
    setPaymentFieldErrors({});
    setDeleteError(null);

    try {
      await createDealMyFinancePayment(dealId, paymentDraft, {
        signal: controller.signal,
      });
      if (controller.signal.aborted || isStale(generation)) return;
      const refreshed = await refetchFinanceAuthoritative(generation);
      if (!refreshed) return;
      setPaymentDraft(emptyPaymentDraft);
      setPaymentSubmitState("idle");
      setPaymentFormOpen(false);
    } catch (error) {
      if (controller.signal.aborted || isRequestAbortError(error)) return;
      if (isStale(generation)) return;
      const parsed = parseDrfFieldErrors(error, [
        "amount",
        "received_at",
        "note",
        "finance",
      ]);
      setPaymentFieldErrors(parsed);
      setPaymentFormError(parsed._form || messageFromError(error));
      setPaymentSubmitState("error");
    } finally {
      if (mutationControllerRef.current === controller) {
        mutationControllerRef.current = null;
      }
      paymentSubmittingRef.current = false;
    }
  };

  const startEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setPaymentFormOpen(false);
    setEditDraft({
      amount: payment.amount || "",
      received_at: payment.received_at || "",
      note: payment.note || "",
    });
    setPaymentFormError(null);
    setPaymentFieldErrors({});
    setDeleteError(null);
  };

  const cancelEditPayment = () => {
    if (paymentBusy) return;
    setEditingPaymentId(null);
    setEditDraft(emptyPaymentDraft);
    setPaymentFormError(null);
    setPaymentFieldErrors({});
  };

  const handlePatchPayment = async (event) => {
    event.preventDefault();
    if (!editingPaymentId || paymentSubmittingRef.current) return;
    paymentSubmittingRef.current = true;
    const generation = mutationGenerationRef.current;
    const controller = new AbortController();
    mutationControllerRef.current = controller;

    setPaymentSubmitState("submitting");
    setPaymentFormError(null);
    setPaymentFieldErrors({});
    setDeleteError(null);

    try {
      await patchDealMyFinancePayment(dealId, editingPaymentId, editDraft, {
        signal: controller.signal,
      });
      if (controller.signal.aborted || isStale(generation)) return;
      const refreshed = await refetchFinanceAuthoritative(generation);
      if (!refreshed) return;
      setEditingPaymentId(null);
      setEditDraft(emptyPaymentDraft);
      setPaymentSubmitState("idle");
    } catch (error) {
      if (controller.signal.aborted || isRequestAbortError(error)) return;
      if (isStale(generation)) return;
      const parsed = parseDrfFieldErrors(error, [
        "amount",
        "received_at",
        "note",
        "finance",
      ]);
      setPaymentFieldErrors(parsed);
      setPaymentFormError(parsed._form || messageFromError(error));
      setPaymentSubmitState("error");
    } finally {
      if (mutationControllerRef.current === controller) {
        mutationControllerRef.current = null;
      }
      paymentSubmittingRef.current = false;
    }
  };

  const handleDeletePaymentRequest = (paymentId) => {
    if (paymentSubmittingRef.current) return;
    setDeleteError(null);
    setPaymentDeleteConfirmId(paymentId);
  };

  const handleCancelDeletePayment = () => {
    if (paymentBusy) return;
    setPaymentDeleteConfirmId(null);
  };

  const handleConfirmDeletePayment = async () => {
    const paymentId = paymentDeleteConfirmId;
    if (paymentId == null || paymentSubmittingRef.current) return;
    paymentSubmittingRef.current = true;
    const generation = mutationGenerationRef.current;
    const controller = new AbortController();
    mutationControllerRef.current = controller;

    setPaymentSubmitState("submitting");
    setPaymentFormError(null);
    setPaymentFieldErrors({});
    setDeleteError(null);
    setPaymentDeleteConfirmId(null);

    try {
      await deleteDealMyFinancePayment(dealId, paymentId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted || isStale(generation)) return;
      const refreshed = await refetchFinanceAuthoritative(generation);
      if (!refreshed) return;
      if (editingPaymentId === paymentId) {
        setEditingPaymentId(null);
        setEditDraft(emptyPaymentDraft);
      }
      setPaymentSubmitState("idle");
    } catch (error) {
      if (controller.signal.aborted || isRequestAbortError(error)) return;
      if (isStale(generation)) return;
      setDeleteError(messageFromError(error));
      setPaymentSubmitState("error");
    } finally {
      if (mutationControllerRef.current === controller) {
        mutationControllerRef.current = null;
      }
      paymentSubmittingRef.current = false;
    }
  };

  const renderIncomePlanFields = ({ boundedChoiceCards = false } = {}) => (
    <>
      <fieldset className="feature-deal-detail__finance-choice">
        <legend>Спосіб розрахунку</legend>
        <div
          className={[
            "feature-deal-detail__finance-choice-cards",
            boundedChoiceCards
              ? "feature-deal-detail__finance-choice-cards--bounded"
              : "",
          ].filter(Boolean).join(" ")}
          role="radiogroup"
          aria-label="Спосіб розрахунку доходу"
        >
          <label
            className="feature-deal-detail__finance-choice-card"
            data-on={financeForm.income_type === "percent" ? "" : undefined}
          >
            <input
              type="radio"
              name={`deal-finance-income-type-${dealId}`}
              value="percent"
              checked={financeForm.income_type === "percent"}
              onChange={() => handleIncomeTypeChange("percent")}
              disabled={financeBusy}
            />
            <span>Відсоток від ціни угоди</span>
          </label>
          <label
            className="feature-deal-detail__finance-choice-card"
            data-on={financeForm.income_type === "fixed" ? "" : undefined}
          >
            <input
              type="radio"
              name={`deal-finance-income-type-${dealId}`}
              value="fixed"
              checked={financeForm.income_type === "fixed"}
              onChange={() => handleIncomeTypeChange("fixed")}
              disabled={financeBusy}
            />
            <span>Фіксована сума</span>
          </label>
          {configured && !hasPayments ? (
            <label
              className="feature-deal-detail__finance-choice-card"
              data-on={financeForm.income_type === "" ? "" : undefined}
              data-testid="deal-private-finance-unset-choice"
            >
              <input
                type="radio"
                name={`deal-finance-income-type-${dealId}`}
                value=""
                checked={financeForm.income_type === ""}
                onChange={() => handleIncomeTypeChange("")}
                disabled={financeBusy}
              />
              <span>Не налаштовано</span>
            </label>
          ) : null}
        </div>
        {configured && !hasPayments && financeForm.income_type !== "" ? (
          <button
            type="button"
            className="action-btn feature-deal-detail__finance-action--secondary"
            data-testid="deal-private-finance-reset-plan"
            onClick={() => handleIncomeTypeChange("")}
            disabled={financeBusy}
          >
            Скинути план
          </button>
        ) : null}
        {configured && hasPayments ? (
          <span className="feature-deal-detail__finance-hint">
            План не можна скинути, поки є історія платежів.
          </span>
        ) : null}
        {financeFieldErrors.income_type ? (
          <InlineError>{financeFieldErrors.income_type}</InlineError>
        ) : null}
      </fieldset>

      {financeForm.income_type === "percent" ? (
        <label className="feature-deal-detail__finance-field">
          <span>Мій відсоток від угоди</span>
          <input
            aria-label="Мій відсоток від угоди"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={financeForm.income_value}
            onChange={(event) => {
              setFinanceSuccess(null);
              setFinanceForm((current) => ({
                ...current,
                income_value: event.target.value,
              }));
            }}
            disabled={financeBusy}
            aria-invalid={financeFieldErrors.income_value ? "true" : undefined}
          />
          <span className="feature-deal-detail__finance-hint">
            Валюта угоди: {dealCurrency || "—"}.
          </span>
          {percentPreview != null ? (
            <span
              className="feature-deal-detail__finance-hint"
              data-testid="deal-private-finance-percent-preview"
            >
              Орієнтовно ≈ {formatMoney(percentPreview, dealCurrency)} до
              збереження.
            </span>
          ) : null}
          {financeFieldErrors.income_value ? (
            <InlineError>{financeFieldErrors.income_value}</InlineError>
          ) : null}
        </label>
      ) : null}

      {financeForm.income_type === "fixed" ? (
        <>
          <label className="feature-deal-detail__finance-field">
            <span>Очікуваний дохід</span>
            <input
              aria-label="Очікуваний дохід"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={financeForm.income_value}
              onChange={(event) => {
                setFinanceSuccess(null);
                setFinanceForm((current) => ({
                  ...current,
                  income_value: event.target.value,
                }));
              }}
              disabled={financeBusy}
              aria-invalid={financeFieldErrors.income_value ? "true" : undefined}
            />
            {financeFieldErrors.income_value ? (
              <InlineError>{financeFieldErrors.income_value}</InlineError>
            ) : null}
          </label>
          <label className="feature-deal-detail__finance-field">
            <span>Валюта доходу</span>
            <select
              aria-label="Валюта доходу"
              value={financeForm.income_currency}
              onChange={(event) => {
                setFinanceSuccess(null);
                setFinanceForm((current) => ({
                  ...current,
                  income_currency: event.target.value,
                }));
              }}
              disabled={financeBusy || hasPayments}
              aria-invalid={financeFieldErrors.income_currency ? "true" : undefined}
            >
              {CURRENCY_OPTIONS.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
            {hasPayments ? (
              <span className="feature-deal-detail__finance-hint">
                Валюту не можна змінити, поки є платежі.
              </span>
            ) : null}
            {financeFieldErrors.income_currency ? (
              <InlineError>{financeFieldErrors.income_currency}</InlineError>
            ) : null}
          </label>
        </>
      ) : null}

      <details
        className="feature-deal-detail__finance-optional-details"
        data-testid="deal-private-finance-optional-details"
      >
        <summary className="feature-deal-detail__finance-optional-summary">
          Додаткові деталі
        </summary>
        <p className="feature-deal-detail__finance-hint">
          Дату або примітку можна зберегти без обраного способу розрахунку.
        </p>
        <div className="feature-deal-detail__finance-optional-fields">
          <label className="feature-deal-detail__finance-field">
            <span>
              Очікувана дата оплати{" "}
              <span className="feature-deal-detail__optional">(необовʼязково)</span>
            </span>
            <input
              aria-label="Очікувана дата оплати"
              type="date"
              value={financeForm.expected_payment_date}
              onChange={(event) => {
                setFinanceSuccess(null);
                setFinanceForm((current) => ({
                  ...current,
                  expected_payment_date: event.target.value,
                }));
              }}
              disabled={financeBusy}
              aria-invalid={
                financeFieldErrors.expected_payment_date ? "true" : undefined
              }
            />
            {financeFieldErrors.expected_payment_date ? (
              <InlineError>
                {financeFieldErrors.expected_payment_date}
              </InlineError>
            ) : null}
          </label>

          <label className="feature-deal-detail__finance-field feature-deal-detail__finance-field--wide">
            <span>
              Особиста примітка{" "}
              <span className="feature-deal-detail__optional">(необовʼязково)</span>
            </span>
            <textarea
              aria-label="Особиста примітка"
              value={financeForm.note}
              onChange={(event) => {
                setFinanceSuccess(null);
                setFinanceForm((current) => ({
                  ...current,
                  note: event.target.value,
                }));
              }}
              maxLength={2000}
              disabled={financeBusy}
            />
            {financeFieldErrors.note ? (
              <InlineError>{financeFieldErrors.note}</InlineError>
            ) : null}
          </label>
        </div>
      </details>
    </>
  );

  const renderFinanceSubmitActions = () => (
    <div className="feature-deal-detail__finance-actions feature-deal-detail__finance-actions--submit">
      <button
        type="submit"
        className="action-btn feature-deal-detail__finance-action--primary"
        disabled={financeBusy || !canSubmitFinance}
        data-testid="deal-private-finance-plan-submit"
      >
        {financeBusy ? "Збереження…" : financeSubmitLabel}
      </button>
    </div>
  );

  return (
    <section
      id="my-finance"
      className="feature-deal-detail__section"
      aria-labelledby="deal-private-finance-heading"
      data-testid="deal-private-finance-section"
    >
      <DealWorkspaceSectionHeader
        id="deal-private-finance-heading"
        eyebrow="Лише для вас"
        title="Мої фінанси"
        privacyBadge="Видно лише вам"
        helper="Особистий дохід і платежі не бачить контрагент і не потрапляють у спільні фінанси угоди."
        iconClassName="fa-solid fa-wallet"
      />

      {loadState === "loading" ? (
        <Spinner size="md" label="Завантаження моїх фінансів" />
      ) : null}

      {loadState === "error" ? (
        <div role="alert">
          <p>{loadError || "Не вдалося завантажити мої фінанси."}</p>
          <button type="button" className="action-btn" onClick={handleRetry}>
            Спробувати ще
          </button>
        </div>
      ) : null}

      {loadState === "loaded" && !configured ? (
        <div
          className="feature-deal-detail__finance-onboarding feature-deal-detail__finance-onboarding--bounded"
          data-testid="deal-private-finance-onboarding"
        >
          <ol className="feature-deal-detail__finance-steps">
            <li>Оберіть спосіб розрахунку доходу.</li>
            <li>Збережіть план.</li>
            <li>Фіксуйте отримані платежі.</li>
          </ol>
          <form
            className="feature-deal-detail__finance-form"
            onSubmit={handleFinanceSubmit}
            data-testid="deal-private-finance-plan-form"
          >
            <div className="feature-deal-detail__finance-plan-workspace">
              <div className="feature-deal-detail__finance-grid">
                {renderIncomePlanFields({ boundedChoiceCards: true })}
              </div>
              {financeFormError ? (
                <InlineError className="feature-deal-detail__finance-message">
                  {financeFormError}
                </InlineError>
              ) : null}
              {financeSuccess ? (
                <p className="feature-deal-detail__status-result">{financeSuccess}</p>
              ) : null}
              {renderFinanceSubmitActions()}
            </div>
          </form>
        </div>
      ) : null}

      {loadState === "loaded" && configured ? (
        <>
          <div
            className="feature-deal-detail__finance-summary"
            data-testid="deal-private-finance-summary"
          >
            <div className="feature-deal-detail__finance-plan-identity">
              <p className="feature-deal-detail__finance-plan-identity-label">
                План доходу
              </p>
              <p className="feature-deal-detail__finance-plan-identity-value">
                <span data-testid="deal-private-finance-plan-type">
                  {getPrivateFinanceIncomeTypeLabel(finance?.income_type || "")}
                </span>
                {finance?.income_type === "percent" && finance?.income_value ? (
                  <span className="feature-deal-detail__finance-plan-identity-detail">
                    {finance.income_value}%
                  </span>
                ) : null}
                {finance?.income_type === "fixed" && finance?.income_value ? (
                  <span className="feature-deal-detail__finance-plan-identity-detail">
                    {formatMoney(finance.income_value, financeCurrency)}
                  </span>
                ) : null}
                {financeCurrency ? (
                  <span
                    className="feature-deal-detail__finance-plan-currency"
                    data-testid="deal-private-finance-effective-currency"
                  >
                    {financeCurrency}
                  </span>
                ) : null}
              </p>
            </div>

            <dl className="feature-deal-detail__facts feature-deal-detail__finance-metrics">
              <div data-finance-metric="expected">
                <dt>Очікується</dt>
                <dd className="feature-deals-list__cell-numeric">
                  {formatMoney(finance?.expected_amount, financeCurrency)}
                </dd>
              </div>
              <div data-finance-metric="received">
                <dt>Отримано</dt>
                <dd className="feature-deals-list__cell-numeric">
                  {formatMoney(finance?.received_total, financeCurrency)}
                </dd>
              </div>
              <div
                data-finance-metric={financeOverpaid ? "overpaid" : "remaining"}
              >
                <dt>
                  {financeOverpaid ? "Переплата" : "Залишилось отримати"}
                </dt>
                <dd className="feature-deals-list__cell-numeric">
                  {financeOverpaid && finance?.outstanding_amount != null
                    ? formatMoney(
                      String(
                        Math.abs(Number(finance.outstanding_amount)),
                      ),
                      financeCurrency,
                    )
                    : formatMoney(finance?.outstanding_amount, financeCurrency)}
                </dd>
              </div>
              <div>
                <dt>Очікувана дата</dt>
                <dd>
                  {formatDateUk(finance?.expected_payment_date)}
                </dd>
              </div>
              <div>
                <dt>Статус</dt>
                <dd>
                  {getPrivateFinancePaymentStatusLabel(finance?.payment_status)}
                </dd>
              </div>
            </dl>
          </div>

          <div
            className="feature-deal-detail__finance-progress"
            data-testid="deal-private-finance-progress"
          >
            <div
              className="feature-deal-detail__finance-progress-track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPercent}
              aria-label="Прогрес отримання доходу"
            >
              <span
                className="feature-deal-detail__finance-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="feature-deal-detail__finance-hint">
              {progressPercent}% від очікуваного доходу
            </p>
          </div>

          {financeOverpaid ? (
            <p
              className="feature-deal-detail__finance-warning"
              role="alert"
              data-testid="deal-private-finance-overpaid-warning"
            >
              Зафіксована переплата. Перевірте суму очікуваного доходу або
              платежі.
            </p>
          ) : null}

          <div className="feature-deal-detail__finance-toolbar">
            <button
              type="button"
              className="action-btn feature-deal-detail__finance-action--primary"
              aria-expanded={planEditorOpen}
              aria-controls="deal-private-finance-plan-editor"
              data-testid="deal-private-finance-edit-plan"
              onClick={() => {
                setFinanceSuccess(null);
                setPlanEditorOpen((open) => !open);
              }}
            >
              Редагувати план
            </button>
            <button
              type="button"
              className="action-btn feature-deal-detail__finance-action--secondary"
              aria-expanded={paymentFormOpen}
              aria-controls="deal-private-finance-payment-create"
              data-testid="deal-private-finance-add-payment"
              onClick={() => {
                setEditingPaymentId(null);
                setPaymentFormError(null);
                setPaymentFieldErrors({});
                setPaymentFormOpen((open) => !open);
              }}
            >
              Додати платіж
            </button>
          </div>

          {showPlanEditor ? (
            <form
              id="deal-private-finance-plan-editor"
              className="feature-deal-detail__finance-form"
              onSubmit={handleFinanceSubmit}
              data-testid="deal-private-finance-plan-form"
            >
              <div className="feature-deal-detail__finance-grid">
                {renderIncomePlanFields()}
              </div>
              {financeFormError ? (
                <InlineError className="feature-deal-detail__finance-message">
                  {financeFormError}
                </InlineError>
              ) : null}
              {financeSuccess ? (
                <p className="feature-deal-detail__status-result">{financeSuccess}</p>
              ) : null}
              <div className="feature-deal-detail__finance-actions">
                {renderFinanceSubmitActions()}
                <button
                  type="button"
                  className="action-btn feature-deal-detail__finance-action--secondary"
                  disabled={financeBusy}
                  onClick={() => {
                    setPlanEditorOpen(false);
                    setFinanceForm(financeToForm(finance, dealCurrency));
                    setFinanceFormError(null);
                    setFinanceFieldErrors({});
                    setFinanceSuccess(null);
                  }}
                >
                  Скасувати
                </button>
              </div>
            </form>
          ) : null}

          <div className="feature-deal-detail__payments feature-deal-detail__payments--finance">
            <h3 className="feature-deal-detail__payments-heading">Історія платежів</h3>
            {payments.length === 0 ? (
              <p className="feature-deal-detail__empty-compact">
                Платежів ще немає.
              </p>
            ) : (
              <ul
                className="feature-deal-detail__timeline"
                data-testid="deal-private-finance-payments"
              >
                {payments.map((payment) => (
                  <li
                    key={payment.id}
                    className="feature-deal-detail__timeline-item"
                    data-testid={`deal-private-finance-payment-${payment.id}`}
                  >
                    <span
                      className="feature-deal-detail__timeline-marker"
                      aria-hidden="true"
                    />
                    <div className="feature-deal-detail__timeline-body">
                      <div className="feature-deal-detail__timeline-title">
                        {formatMoney(payment.amount, financeCurrency)}
                      </div>
                      <div className="feature-deal-detail__timeline-meta">
                        <time dateTime={payment.received_at || undefined}>
                          {formatDateUk(payment.received_at)}
                        </time>
                      </div>
                      {payment.note ? (
                        <div className="feature-deal-detail__timeline-note">
                          {payment.note}
                        </div>
                      ) : null}
                      <div className="feature-deal-detail__payment-actions">
                        <button
                          type="button"
                          className="action-btn"
                          onClick={() => {
                            setPaymentDeleteConfirmId(null);
                            startEditPayment(payment);
                          }}
                          disabled={paymentBusy}
                          data-testid={`deal-private-finance-edit-payment-${payment.id}`}
                        >
                          Редагувати
                        </button>
                        <button
                          type="button"
                          className="action-btn action-btn--danger feature-deal-detail__payment-delete"
                          onClick={() => handleDeletePaymentRequest(payment.id)}
                          disabled={paymentBusy}
                          data-testid={`deal-private-finance-delete-payment-${payment.id}`}
                          aria-expanded={paymentDeleteConfirmId === payment.id}
                        >
                          Видалити
                        </button>
                      </div>
                      {paymentDeleteConfirmId === payment.id ? (
                        <div
                          className="feature-deal-detail__status-confirm feature-deal-detail__payment-delete-confirm"
                          role="alertdialog"
                          aria-label="Підтвердження видалення платежу"
                          data-testid={`deal-private-finance-delete-confirm-${payment.id}`}
                        >
                          <p>Видалити цей платіж? Цю дію не можна скасувати.</p>
                          <div className="feature-deal-detail__status-confirm-actions">
                            <button
                              type="button"
                              className="action-btn"
                              onClick={handleCancelDeletePayment}
                              disabled={paymentBusy}
                            >
                              Скасувати
                            </button>
                            <button
                              type="button"
                              className="action-btn action-btn--danger"
                              onClick={() => void handleConfirmDeletePayment()}
                              disabled={paymentBusy}
                              data-testid={`deal-private-finance-delete-confirm-action-${payment.id}`}
                            >
                              {paymentBusy ? "Видалення…" : "Видалити"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {editingPaymentId ? (
              <form
                className="feature-deal-detail__payment-form"
                onSubmit={handlePatchPayment}
              >
                <h4>Редагувати платіж</h4>
                <label className="feature-deal-detail__finance-field">
                  <span>Сума платежу</span>
                  <input
                    aria-label="Сума платежу"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={editDraft.amount}
                    onChange={(event) =>
                      setEditDraft((current) => ({
                        ...current,
                        amount: event.target.value,
                      }))
                    }
                    disabled={paymentBusy}
                  />
                  {paymentFieldErrors.amount ? (
                    <InlineError>{paymentFieldErrors.amount}</InlineError>
                  ) : null}
                </label>
                <label className="feature-deal-detail__finance-field">
                  <span>Дата отримання</span>
                  <input
                    aria-label="Дата отримання"
                    type="date"
                    value={editDraft.received_at}
                    onChange={(event) =>
                      setEditDraft((current) => ({
                        ...current,
                        received_at: event.target.value,
                      }))
                    }
                    disabled={paymentBusy}
                  />
                  {paymentFieldErrors.received_at ? (
                    <InlineError>{paymentFieldErrors.received_at}</InlineError>
                  ) : null}
                </label>
                <label className="feature-deal-detail__finance-field">
                  <span>Примітка платежу</span>
                  <textarea
                    aria-label="Примітка платежу"
                    value={editDraft.note}
                    onChange={(event) =>
                      setEditDraft((current) => ({
                        ...current,
                        note: event.target.value,
                      }))
                    }
                    maxLength={2000}
                    disabled={paymentBusy}
                  />
                  {paymentFieldErrors.note ? (
                    <InlineError>{paymentFieldErrors.note}</InlineError>
                  ) : null}
                </label>
                {paymentFormError ? (
                  <InlineError>{paymentFormError}</InlineError>
                ) : null}
                <div className="feature-deal-detail__finance-actions">
                  <button
                    type="submit"
                    className="action-btn"
                    disabled={paymentBusy}
                  >
                    {paymentBusy ? "Збереження…" : "Зберегти платіж"}
                  </button>
                  <button
                    type="button"
                    className="action-btn"
                    onClick={cancelEditPayment}
                    disabled={paymentBusy}
                  >
                    Скасувати
                  </button>
                </div>
              </form>
            ) : null}

            {paymentFormOpen && !editingPaymentId ? (
              <form
                id="deal-private-finance-payment-create"
                className="feature-deal-detail__payment-form"
                onSubmit={handleCreatePayment}
                data-testid="deal-private-finance-payment-form"
              >
                <h4>Додати платіж</h4>
                <label className="feature-deal-detail__finance-field">
                  <span>Сума платежу</span>
                  <input
                    aria-label="Сума платежу"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={paymentDraft.amount}
                    onChange={(event) =>
                      setPaymentDraft((current) => ({
                        ...current,
                        amount: event.target.value,
                      }))
                    }
                    disabled={paymentBusy}
                  />
                  {paymentFieldErrors.amount ? (
                    <InlineError>{paymentFieldErrors.amount}</InlineError>
                  ) : null}
                </label>
                <label className="feature-deal-detail__finance-field">
                  <span>Дата отримання</span>
                  <input
                    aria-label="Дата отримання"
                    type="date"
                    value={paymentDraft.received_at}
                    onChange={(event) =>
                      setPaymentDraft((current) => ({
                        ...current,
                        received_at: event.target.value,
                      }))
                    }
                    disabled={paymentBusy}
                  />
                  {paymentFieldErrors.received_at ? (
                    <InlineError>{paymentFieldErrors.received_at}</InlineError>
                  ) : null}
                </label>
                <label className="feature-deal-detail__finance-field">
                  <span>Примітка платежу</span>
                  <textarea
                    aria-label="Примітка платежу"
                    value={paymentDraft.note}
                    onChange={(event) =>
                      setPaymentDraft((current) => ({
                        ...current,
                        note: event.target.value,
                      }))
                    }
                    maxLength={2000}
                    disabled={paymentBusy}
                  />
                  {paymentFieldErrors.note ? (
                    <InlineError>{paymentFieldErrors.note}</InlineError>
                  ) : null}
                </label>
                {paymentFieldErrors.finance ? (
                  <InlineError>{paymentFieldErrors.finance}</InlineError>
                ) : null}
                {paymentFormError ? (
                  <InlineError>{paymentFormError}</InlineError>
                ) : null}
                {deleteError ? <InlineError>{deleteError}</InlineError> : null}
                <div className="feature-deal-detail__finance-actions">
                  <button
                    type="submit"
                    className="action-btn"
                    disabled={paymentBusy}
                  >
                    {paymentBusy ? "Додавання…" : "Додати платіж"}
                  </button>
                  <button
                    type="button"
                    className="action-btn"
                    disabled={paymentBusy}
                    onClick={() => {
                      setPaymentFormOpen(false);
                      setPaymentDraft(emptyPaymentDraft);
                      setPaymentFormError(null);
                      setPaymentFieldErrors({});
                    }}
                  >
                    Скасувати
                  </button>
                </div>
              </form>
            ) : null}

            {deleteError && !paymentFormOpen ? (
              <InlineError>{deleteError}</InlineError>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}

export default DealPrivateFinanceSection;
